import * as React from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { downloadAttachment } from '@/data/advisor-chat';
import type { ChatAttachment } from '@/types/chat';

const PREVIEW_IMAGE = new Set([
  'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/avif',
]);
const PREVIEW_VIDEO = new Set([
  'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
]);

function kind(attachment: ChatAttachment): 'image' | 'video' | 'file' {
  const type = (attachment.mime_type || '').toLowerCase();
  if (PREVIEW_IMAGE.has(type)) return 'image';
  if (PREVIEW_VIDEO.has(type)) return 'video';

  const name = attachment.filename.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif)$/.test(name)) return 'image';
  if (/\.(mp4|webm|ogv|mov)$/.test(name)) return 'video';
  return 'file';
}

function humanSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function usePreviewUrl(attachment: ChatAttachment, wanted: 'image' | 'video') {
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const api = await authenticatedBitswanBackendInstance();
        const res = await api.get(`/advisor-chat/attachments/${attachment.id}/`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        const type =
          attachment.mime_type && attachment.mime_type !== 'application/octet-stream'
            ? attachment.mime_type
            : `${wanted}/${attachment.filename.split('.').pop()}`;
        objectUrl = URL.createObjectURL(
          new Blob([res.data as Blob], { type }),
        );
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.mime_type, attachment.filename, wanted]);

  return { url, failed };
}

function Preview({ attachment, as }: { attachment: ChatAttachment; as: 'image' | 'video' }) {
  const { url, failed } = usePreviewUrl(attachment, as);

  if (failed) return <FileRow attachment={attachment} />;

  if (!url) {
    return (
      <div className="flex h-24 items-center justify-center rounded bg-black/5">
        <Loader2 className="h-4 w-4 animate-spin opacity-60" />
      </div>
    );
  }

  return (
    <figure className="space-y-1" data-testid={`chat-preview-${attachment.filename}`}>
      {as === 'image' ? (
        <img
          src={url}
          alt={attachment.filename}
          className="max-h-64 w-auto max-w-full rounded"
          onError={() => undefined}
        />
      ) : (
        <video
          src={url}
          controls
          className="max-h-64 w-full rounded"
          data-testid={`chat-video-${attachment.filename}`}
        />
      )}
      <figcaption>
        <button
          type="button"
          onClick={() =>
            downloadAttachment(attachment.id, attachment.filename).catch(() =>
              toast.error('Could not download that file.'),
            )
          }
          className="flex items-center gap-1 text-[11px] underline opacity-80"
          data-testid={`chat-attachment-${attachment.filename}`}
        >
          <Download className="h-3 w-3" />
          {attachment.filename} ({humanSize(attachment.file_size)})
        </button>
      </figcaption>
    </figure>
  );
}

function FileRow({ attachment }: { attachment: ChatAttachment }) {
  return (
    <button
      type="button"
      onClick={() =>
        downloadAttachment(attachment.id, attachment.filename).catch(() =>
          toast.error('Could not download that file.'),
        )
      }
      className="flex items-center gap-1.5 text-xs underline"
      data-testid={`chat-attachment-${attachment.filename}`}
    >
      <Download className="h-3 w-3 shrink-0" />
      <span className="truncate">{attachment.filename}</span>
      <span className="opacity-70">({humanSize(attachment.file_size)})</span>
    </button>
  );
}

export default function AttachmentView({ attachment }: { attachment: ChatAttachment }) {
  const shape = kind(attachment);
  if (shape === 'file') return <FileRow attachment={attachment} />;
  return <Preview attachment={attachment} as={shape} />;
}
